import { mulberry32, pick, type Rng } from '@gazette/shared';
import { INK, PHOTO_PAPER } from './palette.ts';

// Bertillon-card profile portraits, drawn from a seed. The face is painted as a grayscale
// tone map, then reprinted through a 45° halftone screen, the way newspapers reproduced
// police photographs. Everything is authored here; no image assets ship with the game.

type Hair = 'short' | 'bun' | 'curly' | 'long' | 'bald' | 'quiff' | 'bob' | 'braid';
type Hat = 'bowler' | 'tophat' | 'beret' | 'cloche' | 'boater';
type Facial = 'mustache' | 'handlebar' | 'beard' | 'goatee' | 'sideburns';
type Eyewear = 'monocle' | 'glasses' | 'pincenez';
type Collar = 'collar' | 'cravat' | 'bow' | 'ruffle' | 'turtleneck' | 'pearls';
type Nose = 'straight' | 'aquiline' | 'button' | 'long' | 'snub';

interface Features {
  back: number;
  crown: number;
  brow: number;
  nose: Nose;
  noseLen: number;
  lip: number;
  chin: number;
  jaw: number;
  hair: Hair;
  hat: Hat | null;
  facial: Facial | null;
  eyewear: Eyewear | null;
  collar: Collar;
}

function features(seed: number): Features {
  const rng: Rng = mulberry32(seed);
  const hat = rng() < 0.35 ? pick<Hat>(rng, ['bowler', 'tophat', 'beret', 'cloche', 'boater']) : null;
  return {
    back: 23 + rng() * 6,
    crown: 17 + rng() * 7,
    brow: 73 + rng() * 4,
    nose: pick<Nose>(rng, ['straight', 'aquiline', 'button', 'long', 'snub']),
    noseLen: 7 + rng() * 7,
    lip: rng() * 2.4,
    chin: -2 + rng() * 5,
    jaw: 88 + rng() * 5,
    hair: pick<Hair>(rng, ['short', 'bun', 'curly', 'long', 'bald', 'quiff', 'bob', 'braid']),
    hat,
    facial: rng() < 0.4 ? pick<Facial>(rng, ['mustache', 'handlebar', 'beard', 'goatee', 'sideburns']) : null,
    eyewear: rng() < 0.25 ? pick<Eyewear>(rng, ['monocle', 'glasses', 'pincenez']) : null,
    collar: pick<Collar>(rng, ['collar', 'cravat', 'bow', 'ruffle', 'turtleneck', 'pearls']),
  };
}

/** Darkness 0 (paper) to 1 (solid ink), as a gray fill for the tone map. */
const tone = (darkness: number) => {
  const v = Math.round(255 * (1 - darkness));
  return `rgb(${v},${v},${v})`;
};

function headPath(f: Features): Path2D {
  const { back: b, crown: c, brow: br, noseLen: n, lip, chin, jaw } = f;
  const p = new Path2D();
  p.moveTo(40, 125);
  p.lineTo(41, 100);
  p.bezierCurveTo(38, 92, b - 2, 80, b, 62);
  p.bezierCurveTo(b + 1, 36, 38, c, 55, c);
  p.bezierCurveTo(66, c, br - 3, c + 8, br - 1, 36);
  p.bezierCurveTo(br + 0.5, 40, br + 1, 44, br, 47);
  p.lineTo(br - 1.5, 50);
  switch (f.nose) {
    case 'straight':
      p.lineTo(br + n, 62);
      p.lineTo(br + 1, 64.5);
      break;
    case 'aquiline':
      p.bezierCurveTo(br + n * 0.6, 51, br + n, 57, br + n - 1, 62);
      p.bezierCurveTo(br + n - 3, 64, br + 3, 64.5, br + 1, 64.5);
      break;
    case 'button':
      p.bezierCurveTo(br + 2, 55, br + n * 0.7, 58, br + n * 0.7, 61);
      p.bezierCurveTo(br + n * 0.7, 64, br + 2, 64.5, br + 1, 64.5);
      break;
    case 'long':
      p.lineTo(br + n + 2, 64);
      p.lineTo(br + 1, 66);
      break;
    case 'snub':
      p.bezierCurveTo(br + 1, 56, br + n * 0.8, 59, br + n * 0.8, 60);
      p.lineTo(br + n * 0.5, 63);
      p.lineTo(br + 1, 64.5);
      break;
  }
  p.bezierCurveTo(br + 1.5, 67, br + 2 + lip, 68, br + 2 + lip, 69.5);
  p.lineTo(br + 0.5, 71.5);
  p.bezierCurveTo(br + 1.5 + lip, 72.5, br + 1.5 + lip, 74.5, br, 76);
  p.bezierCurveTo(br - 2.5, 77.5, br - 2.5 + chin, 79, br - 1 + chin, 82);
  p.bezierCurveTo(br - 1 + chin, 86, br - 6, jaw, br - 12, jaw);
  p.bezierCurveTo(br - 16, jaw + 1, 60, 95, 60, 100);
  p.lineTo(62, 125);
  p.closePath();
  return p;
}

function hairPath(f: Features): Path2D {
  const { back: b, crown: c, brow: br } = f;
  const p = new Path2D();
  const cap = () => {
    p.moveTo(br - 3, 35);
    p.bezierCurveTo(br - 6, c - 2, 44, c - 4, b + 2, c + 18);
    p.bezierCurveTo(b - 3, 48, b - 2, 60, b + 1, 67);
    p.lineTo(45, 67);
    p.bezierCurveTo(47, 54, 54, 44, br - 7, 38);
    p.closePath();
  };
  switch (f.hair) {
    case 'bald':
      p.moveTo(b + 0.5, 50);
      p.bezierCurveTo(b - 1.5, 58, b + 1, 66, b + 2, 67);
      p.lineTo(44, 66);
      p.bezierCurveTo(44, 58, b + 6, 52, b + 0.5, 50);
      p.closePath();
      break;
    case 'long':
      p.moveTo(br - 3, 35);
      p.bezierCurveTo(br - 6, c - 3, 42, c - 5, b + 1, c + 18);
      p.bezierCurveTo(b - 6, 56, b - 6, 84, b - 3, 104);
      p.bezierCurveTo(36, 106, 44, 106, 48, 102);
      p.bezierCurveTo(44, 90, 45, 74, 48, 66);
      p.bezierCurveTo(49, 54, 55, 44, br - 7, 38);
      p.closePath();
      break;
    case 'bob':
      p.moveTo(br - 3, 35);
      p.bezierCurveTo(br - 6, c - 3, 42, c - 5, b + 1, c + 18);
      p.bezierCurveTo(b - 5, 56, b - 4, 78, b + 2, 88);
      p.bezierCurveTo(44, 90, 52, 88, 56, 84);
      p.bezierCurveTo(52, 74, 52, 56, br - 7, 40);
      p.closePath();
      break;
    case 'quiff':
      cap();
      p.moveTo(br - 12, c + 4);
      p.bezierCurveTo(br - 10, c - 8, br + 2, c - 6, br - 1, 34);
      p.lineTo(br - 9, 36);
      p.closePath();
      break;
    case 'bun':
      cap();
      p.moveTo(b + 10, c + 8);
      p.arc(b + 3, c + 10, 8, 0, Math.PI * 2);
      break;
    case 'braid':
      cap();
      for (let i = 0; i < 5; i++) {
        const y = 66 + i * 8;
        p.moveTo(b + 6, y);
        p.ellipse(b + 2 - i * 0.6, y, 4, 4.6, 0.3, 0, Math.PI * 2);
      }
      break;
    case 'curly':
      cap();
      for (let i = 0; i < 11; i++) {
        const a = Math.PI * (0.95 + i * 0.1);
        const x = 55 + Math.cos(a) * 30;
        const y = 50 + Math.sin(a) * 32;
        p.moveTo(x + 5, y);
        p.arc(x, y, 5, 0, Math.PI * 2);
      }
      break;
    case 'short':
      cap();
      break;
  }
  return p;
}

function drawHat(ctx: CanvasRenderingContext2D, f: Features): void {
  const c = f.crown;
  ctx.fillStyle = tone(0.95);
  switch (f.hat) {
    case 'tophat':
      ctx.fillRect(35, c - 28, 38, 36);
      ctx.fillStyle = tone(0.72);
      ctx.fillRect(35, c + 1, 38, 5);
      ctx.fillStyle = tone(0.95);
      ctx.beginPath();
      ctx.ellipse(54, c + 8, 31, 3.6, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'bowler':
      ctx.beginPath();
      ctx.ellipse(54, c + 8, 22, 17, 0, Math.PI, 0);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(54, c + 9, 29, 3.4, -0.04, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'beret':
      ctx.beginPath();
      ctx.ellipse(52, c + 3, 26, 8, -0.16, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(50, c - 8, 2.4, 5);
      break;
    case 'cloche':
      ctx.beginPath();
      ctx.moveTo(f.brow + 2, 42);
      ctx.bezierCurveTo(f.brow - 2, c - 10, 34, c - 12, f.back - 3, 60);
      ctx.lineTo(f.back + 6, 62);
      ctx.bezierCurveTo(48, 50, 62, 40, f.brow + 2, 42);
      ctx.fill();
      ctx.fillStyle = tone(0.6);
      ctx.fillRect(44, c + 10, 26, 3);
      break;
    case 'boater':
      ctx.fillStyle = tone(0.35);
      ctx.fillRect(36, c - 7, 36, 14);
      ctx.fillStyle = tone(0.92);
      ctx.fillRect(36, c + 1, 36, 5);
      ctx.fillStyle = tone(0.4);
      ctx.beginPath();
      ctx.ellipse(54, c + 7, 33, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    case null:
      break;
  }
}

function drawFacialHair(ctx: CanvasRenderingContext2D, f: Features): void {
  const br = f.brow;
  ctx.fillStyle = tone(0.9);
  switch (f.facial) {
    case 'mustache':
    case 'handlebar':
      ctx.beginPath();
      ctx.moveTo(br + 1.5, 65.2);
      ctx.bezierCurveTo(br - 1, 64.5, br - 6, 66, br - 7, 69);
      ctx.bezierCurveTo(br - 4, 68.5, br - 1, 68.5, br + 2, 68.4);
      ctx.fill();
      if (f.facial === 'handlebar') {
        ctx.beginPath();
        ctx.moveTo(br + 1.5, 66);
        ctx.bezierCurveTo(br + 5, 67, br + 6, 64, br + 4.5, 62.5);
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = tone(0.9);
        ctx.stroke();
      }
      break;
    case 'beard':
      ctx.beginPath();
      ctx.moveTo(50, 70);
      ctx.bezierCurveTo(53, 82, 60, 90, br - 10, f.jaw + 2);
      ctx.bezierCurveTo(br - 3, f.jaw + 1, br + f.chin, 88, br - 1 + f.chin, 81);
      ctx.lineTo(br - 1, 76);
      ctx.bezierCurveTo(br - 6, 77, br - 12, 74, br - 16, 70);
      ctx.closePath();
      ctx.fill();
      break;
    case 'goatee':
      ctx.beginPath();
      ctx.moveTo(br - 1, 76.5);
      ctx.bezierCurveTo(br - 3, 80, br - 2 + f.chin, 86, br - 1 + f.chin, 88);
      ctx.bezierCurveTo(br - 6, 86, br - 7, 80, br - 5, 77);
      ctx.closePath();
      ctx.fill();
      break;
    case 'sideburns':
      ctx.beginPath();
      ctx.moveTo(49, 62);
      ctx.lineTo(54, 62);
      ctx.lineTo(55, 76);
      ctx.lineTo(51, 78);
      ctx.closePath();
      ctx.fill();
      break;
    case null:
      break;
  }
}

function drawEyewear(ctx: CanvasRenderingContext2D, f: Features): void {
  if (!f.eyewear) return;
  const x = f.brow - 3;
  ctx.strokeStyle = tone(0.97);
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.arc(x, 51, f.eyewear === 'pincenez' ? 2.8 : 3.8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  if (f.eyewear === 'glasses') {
    ctx.moveTo(x - 3.8, 51);
    ctx.lineTo(49, 56);
  } else {
    ctx.moveTo(x, 54.8);
    ctx.quadraticCurveTo(x - 6, 70, x - 10, 84);
  }
  ctx.stroke();
}

function drawCollar(ctx: CanvasRenderingContext2D, f: Features): void {
  switch (f.collar) {
    case 'turtleneck':
      ctx.fillStyle = tone(0.8);
      ctx.beginPath();
      ctx.moveTo(40, 96);
      ctx.lineTo(62, 94);
      ctx.lineTo(64, 110);
      ctx.lineTo(39, 110);
      ctx.closePath();
      ctx.fill();
      break;
    case 'ruffle':
      ctx.fillStyle = tone(0.1);
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc(62 + (i % 2) * 3, 101 + i * 4, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'pearls':
      ctx.fillStyle = tone(0.08);
      for (let i = 0; i < 7; i++) {
        ctx.beginPath();
        ctx.arc(43 + i * 3.1, 101 + Math.sin((i / 6) * Math.PI) * 4, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    default: {
      ctx.fillStyle = tone(0.08);
      ctx.beginPath();
      ctx.moveTo(58, 99);
      ctx.lineTo(70, 103);
      ctx.lineTo(62, 111);
      ctx.lineTo(56, 104);
      ctx.closePath();
      ctx.fill();
      if (f.collar === 'cravat') {
        ctx.fillStyle = tone(0.75);
        ctx.beginPath();
        ctx.moveTo(62, 103);
        ctx.lineTo(68, 105);
        ctx.lineTo(66, 118);
        ctx.lineTo(61, 116);
        ctx.closePath();
        ctx.fill();
      } else if (f.collar === 'bow') {
        ctx.fillStyle = tone(0.9);
        ctx.beginPath();
        ctx.moveTo(64, 105);
        ctx.lineTo(71, 101);
        ctx.lineTo(71, 109);
        ctx.closePath();
        ctx.moveTo(64, 105);
        ctx.lineTo(58, 101.5);
        ctx.lineTo(58.5, 108.5);
        ctx.closePath();
        ctx.fill();
      }
    }
  }
}

function paintToneMap(ctx: CanvasRenderingContext2D, f: Features): void {
  const paper = ctx.createLinearGradient(0, 0, 0, 125);
  paper.addColorStop(0, tone(0.03));
  paper.addColorStop(1, tone(0.12));
  ctx.fillStyle = paper;
  ctx.fillRect(0, 0, 100, 125);

  ctx.fillStyle = tone(0.84);
  ctx.beginPath();
  ctx.moveTo(14, 125);
  ctx.bezierCurveTo(16, 108, 30, 101, 46, 99);
  ctx.lineTo(68, 99);
  ctx.bezierCurveTo(84, 102, 94, 112, 98, 125);
  ctx.closePath();
  ctx.fill();

  const skin = ctx.createLinearGradient(30, 0, 88, 0);
  skin.addColorStop(0, tone(0.7));
  skin.addColorStop(0.7, tone(0.46));
  skin.addColorStop(1, tone(0.36));
  ctx.fillStyle = skin;
  ctx.fill(headPath(f));

  ctx.fillStyle = tone(0.32);
  ctx.beginPath();
  ctx.ellipse(f.brow - 10, 63, 7, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = tone(0.74);
  ctx.beginPath();
  ctx.ellipse(48, 60, 4.2, 6.8, 0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = tone(0.9);
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.ellipse(48.5, 60, 2.2, 4.2, 0.15, -1.2, 1.9);
  ctx.stroke();

  ctx.fillStyle = tone(0.96);
  ctx.beginPath();
  ctx.ellipse(f.brow - 4.5, 51, 2.4, 1.1, 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = tone(0.9);
  ctx.beginPath();
  ctx.moveTo(f.brow - 8.5, 46.5);
  ctx.quadraticCurveTo(f.brow - 4, 45, f.brow - 0.5, 46.8);
  ctx.stroke();

  drawCollar(ctx, f);
  if (!(f.hat === 'tophat' || f.hat === 'bowler' || f.hat === 'boater' || f.hat === 'cloche') || f.hair === 'long' || f.hair === 'bob') {
    ctx.fillStyle = tone(0.93);
    ctx.fill(hairPath(f));
  } else {
    ctx.fillStyle = tone(0.93);
    ctx.fill(hairPath({ ...f, hair: 'bald' }));
  }
  drawFacialHair(ctx, f);
  drawEyewear(ctx, f);
  drawHat(ctx, f);
}

function halftone(source: HTMLCanvasElement, target: CanvasRenderingContext2D, cell: number): void {
  const { width, height } = source;
  const pixels = source.getContext('2d')!.getImageData(0, 0, width, height).data;
  target.fillStyle = PHOTO_PAPER;
  target.fillRect(0, 0, width, height);
  target.fillStyle = INK;
  const cos = Math.SQRT1_2;
  const reach = Math.ceil((width + height) / cell);
  for (let i = -reach; i <= reach; i++) {
    for (let j = -reach; j <= reach; j++) {
      const x = width / 2 + (i - j) * cell * cos;
      const y = height / 2 + (i + j) * cell * cos;
      if (x < -cell || y < -cell || x > width + cell || y > height + cell) continue;
      const sx = Math.min(width - 1, Math.max(0, Math.round(x)));
      const sy = Math.min(height - 1, Math.max(0, Math.round(y)));
      const darkness = 1 - pixels[(sy * width + sx) * 4]! / 255;
      const radius = cell * 0.56 * Math.sqrt(darkness);
      if (radius < 0.35) continue;
      target.beginPath();
      target.arc(x, y, radius, 0, Math.PI * 2);
      target.fill();
    }
  }
}

function drawRuler(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.strokeStyle = INK;
  ctx.lineWidth = Math.max(1, width / 160);
  ctx.strokeRect(ctx.lineWidth / 2, ctx.lineWidth / 2, width - ctx.lineWidth, height - ctx.lineWidth);
  const ticks = 25;
  for (let i = 1; i < ticks; i++) {
    const y = (height / ticks) * i;
    const long = i % 5 === 0;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width * (long ? 0.06 : 0.03), y);
    ctx.stroke();
  }
}

const cache = new Map<string, HTMLCanvasElement>();

/** Returns a cached canvas; callers copy it with drawImage and never draw into it. */
export function portraitCanvas(seed: number, width: number, height = Math.round(width * 1.25)): HTMLCanvasElement {
  const key = `${seed}:${width}x${height}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const toneMap = document.createElement('canvas');
  toneMap.width = width;
  toneMap.height = height;
  const toneCtx = toneMap.getContext('2d', { willReadFrequently: true })!;
  toneCtx.scale(width / 100, height / 125);
  paintToneMap(toneCtx, features(seed));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  halftone(toneMap, ctx, Math.max(2.6, width / 42));
  drawRuler(ctx, width, height);
  cache.set(key, canvas);
  return canvas;
}
